import type { Entry } from "../types";

/**
 * The AI quality screen and the two review-time controls that feed it. Wording
 * matters more than usual here: these labels are what makes a reviewer
 * understand that pressing a button is a measurement rather than a comment.
 */
export const aiQualityMessages = {
  // Review-time capture
  findingDisposition: { ko: "지적사항 판정", en: "Finding disposition" },
  missedFindings: { ko: "AI가 놓친 사항", en: "What the analysis missed" },
  missedFindingsHelp: {
    ko: "AI가 잡지 못한 위반 사항을 지금 기록해 주세요. 놓친 사항은 지적사항이 생성되지 않으므로 다른 곳에 기록이 남지 않으며, 이 기록이 Educate Sage 학습의 출발점이 됩니다.",
    en: "Record anything the analysis failed to catch. A miss creates no finding, so it leaves no other trace — and this record is where an Educate Sage lesson starts.",
  },
  missedFindingsPlaceholder: {
    ko: "예: 상품 약관 확인 권유 문구 누락",
    en: "e.g. missing instruction to read the policy terms",
  },
  addMissedFinding: { ko: "놓친 사항 추가", en: "Add miss" },
  remove: { ko: "제거", en: "Remove" },

  // Why a review was routed where it was — shown to the requester, because a
  // clean result that still went to a reviewer otherwise looks arbitrary.
  routingWhy: { ko: "이 결과가 나온 이유", en: "Why this outcome" },
  routingSelfCheckClear: {
    ko: "자동 자체점검으로 완료되었습니다. 심의자가 개별적으로 확인하지 않았습니다.",
    en: "Completed as an automatic self-check. No reviewer looked at it individually.",
  },
  routingSelfCheckRejected: {
    ko: "자동 자체점검으로 반려되었습니다. 수정 후 다시 제출해 주세요.",
    en: "Rejected as an automatic self-check. Correct the material and resubmit.",
  },
  routingQcSample: {
    ko: "품질 점검 표본으로 추출되었습니다. 분석 결과와 무관하게 심의자가 한 번 더 확인합니다.",
    en: "Selected as a quality-control sample. A reviewer checks it once more regardless of the result.",
  },
  routingBaselineOnly: {
    ko: "승인된 사내 기준이 없어 기본 참고자료만 적용되었습니다. 이 결과만으로는 통과 처리할 수 없어 심의자가 확인합니다.",
    en: "No approved internal standard was available, so only baseline references applied. That result cannot clear on its own, so a reviewer confirms it.",
  },
  routingActionRequired: {
    ko: "지적사항이 있어 수정 또는 심의자 확인이 필요합니다.",
    en: "Findings need correction or a reviewer's confirmation.",
  },
  routingEscalated: {
    ko: "심의자 확인이 필요합니다.",
    en: "A reviewer needs to confirm this.",
  },
  routingProcessingFailed: {
    ko: "분석을 끝까지 완료하지 못했습니다. 심의자 확인이 필요합니다.",
    en: "The analysis did not run to completion, so a reviewer needs to confirm this.",
  },

  // Screen
  navAiQuality: { ko: "AI 품질", en: "AI Quality" },
  aiQuality: { ko: "AI 품질", en: "AI Quality" },
  aiQualityHelp: {
    ko: "자동 판정을 켤 수 있는지, 어떤 기준이 오탐을 만드는지, AI가 무엇을 놓치는지 — 사람이 내린 결정만으로 계산합니다. AI가 스스로 보고한 확신도는 사용하지 않습니다.",
    en: "Whether automatic decisions may be switched on, which standards produce wrong findings, and what the analysis misses — computed only from human decisions. The model's own confidence is never an input.",
  },
  aiQualityWindow: { ko: "집계 기간", en: "Window" },
  aiQualityWindowDays: { ko: "일", en: "days" },
  aiQualityEngineLineage: { ko: "엔진 계열", en: "Engine lineage" },
  aiQualityEngineLineageHelp: {
    ko: "프롬프트나 모델이 바뀌면 이전 수치는 더 이상 현재 시스템을 설명하지 않으므로, 같은 계열의 실행만 집계합니다.",
    en: "Only runs from the current lineage are counted: after a prompt or model change, earlier numbers no longer describe the system that is running.",
  },
  aiQualityTruncated: {
    ko: "집계 한도에 도달해 일부 실행이 제외되었습니다. 기간을 줄여 다시 확인해 주세요.",
    en: "The row limit was reached and some runs were left out. Narrow the window and check again.",
  },

  // Summary tiles
  aiQualityCoverage: { ko: "자동 처리 비율", en: "Automatically resolved" },
  aiQualityMeasuredClear: { ko: "검증된 통과 건수", en: "Measured clear results" },
  aiQualityFalseClear: { ko: "오승인", en: "False clears" },
  aiQualityFalseClearHelp: {
    ko: "AI가 통과시켰지만 심의자가 원안대로 승인하지 않은 건수입니다. 이 프로그램이 막으려는 유일한 실패입니다.",
    en: "Clear results a reviewer did not approve as-is. The one failure this whole program exists to prevent.",
  },
  aiQualityBound: { ko: "오승인률 상한 (95%)", en: "False-clear bound (95%)" },
  aiQualityFalseFlags: { ko: "오탐", en: "False flags" },
  aiQualityUndispositioned: { ko: "판정되지 않은 지적", en: "Unlabelled findings" },
  aiQualityUndispositionedHelp: {
    ko: "판정이 입력되지 않은 지적이 많으면 위 수치는 근거의 일부만 설명합니다.",
    en: "While many findings carry no disposition, the numbers above describe only part of the evidence.",
  },

  // Readiness tab
  aiQualityReadiness: { ko: "자동 승인 준비도", en: "Auto-clear readiness" },
  aiQualityReadinessHelp: {
    ko: "매체 × 구분별로, 근거가 자동 승인을 허용하는지 보여줍니다. 최소 {min}건의 결정이 필요하고 오승인률 상한이 {target} 이하여야 합니다.",
    en: "Per channel × category, whether the evidence permits automatic clearance: at least {min} decisions and a false-clear bound at or under {target}.",
  },
  aiQualityCell: { ko: "매체 × 구분", en: "Channel × category" },
  aiQualityDecided: { ko: "결정 건수", en: "Decisions" },
  aiQualityShortfall: { ko: "부족", en: "Short by" },
  aiQualityEnabledWithoutEvidence: {
    ko: "근거 없이 켜져 있음",
    en: "On without evidence",
  },
  aiQualityNoCells: {
    ko: "이 기간에 분석된 자료가 없습니다.",
    en: "No material was analysed in this window.",
  },

  // False-flag tab
  aiQualityFalseFlagSources: { ko: "오탐 순위", en: "False-flag sources" },
  aiQualityFalseFlagSourcesHelp: {
    ko: "통과 판정에는 지적이 0건이어야 하므로, 잘못된 지적 하나가 자동 승인 한 건을 없앱니다. 자동 처리 비율을 올리는 가장 큰 수단은 여기 상위 항목을 좁히거나 폐기하는 것입니다.",
    en: "A clear result requires zero findings, so one wrong finding removes one automatic clearance. Narrowing or retiring the top rows here is the largest lever on coverage.",
  },
  aiQualitySource: { ko: "기준", en: "Source" },
  aiQualityFalseFlagRate: { ko: "오탐율", en: "False-flag rate" },
  aiQualityConfirmed: { ko: "인정", en: "Confirmed" },
  aiQualityNotApplicable: { ko: "해당 없음", en: "Not applicable" },
  aiQualityNoFalseFlags: {
    ko: "판정된 오탐이 없습니다. 심의 화면에서 지적사항 판정을 입력하면 여기에 집계됩니다.",
    en: "No false flags recorded yet. Dispositions entered on the review screen appear here.",
  },

  // Missed findings tab
  aiQualityMissed: { ko: "AI 미탐 목록", en: "Missed findings" },
  aiQualityMissedHelp: {
    ko: "심의자가 잡았지만 AI가 놓친 사항입니다. Educate Sage 학습으로 전환하면 같은 공백이 반복되지 않습니다. 자동 통과 대상이었던 건은 별도로 표시됩니다.",
    en: "What reviewers caught and the analysis did not. Turning one into an Educate Sage lesson is what stops the gap recurring; misses on material that would have cleared automatically are marked separately.",
  },
  aiQualityWouldHaveCleared: { ko: "자동 통과 대상이었음", en: "Would have auto-cleared" },
  aiQualityTaught: { ko: "학습 완료", en: "Taught" },
  aiQualityNoMissed: {
    ko: "기록된 미탐이 없습니다.",
    en: "No misses recorded.",
  },

  // Engine versions (platform)
  aiQualityEngineVersions: { ko: "엔진 버전별 품질", en: "Quality by engine version" },
  aiQualityEngineVersionsHelp: {
    ko: "프롬프트나 모델을 바꾼 결과가 나빠졌는지는 하나로 합친 수치로는 물을 수 없습니다. 배포 전 이 표를 비교하세요.",
    en: "Whether a prompt or model change made things worse cannot be asked of a single blended number. Compare this table before deploying.",
  },
  aiQualityLineage: { ko: "엔진 세대", en: "Lineage" },
  aiQualityPromptHash: { ko: "프롬프트 해시", en: "Prompt hash" },
  aiQualityBuilds: { ko: "빌드", en: "Build" },
  aiQualityRuns: { ko: "실행", en: "Runs" },
  aiQualitySchemaInvalid: { ko: "스키마 실패", en: "Schema invalid" },
  aiQualityLastRun: { ko: "최근 실행", en: "Last run" },
  aiQualityCurrent: { ko: "현재", en: "Current" },
  aiQualityUnattributed: { ko: "버전 미기록", en: "Unversioned" },
  navEngineQuality: { ko: "엔진 버전별 품질", en: "Engine version quality" },
  engineQualityIntro: {
    ko: "엔진과 프롬프트 버전별로 실제 결과가 어땠는지 비교합니다. 전체 조직을 함께 봅니다 — 프롬프트나 모델 변경으로 생긴 저하는 평균이 움직이기 전에 특정 고객의 수치에서 먼저 나타납니다.",
    en: "Compare how each engine and prompt version actually behaved, across every organization: a regression from a prompt or model change shows up in one customer's numbers before it moves any average.",
  },
  engineQualityCardHelp: {
    ko: "배포한 변경이 결과를 나쁘게 만들었는지 버전별로 비교합니다.",
    en: "Compare versions to see whether a change you shipped made results worse.",
  },
  engineQualityDataNote: {
    ko: "오승인과 상한은 심의자가 결정한 건에서만 계산됩니다. 해당 버전에서 AI가 통과시킨 자료를 사람이 결정한 이력이 쌓이기 전까지는 0과 '-'로 표시되는 것이 정상입니다.",
    en: "False clears and the bound are computed only from human decisions. Until reviewers have decided material this version cleared, 0 and “-” are the correct readings, not a broken panel.",
  },
} satisfies Record<string, Entry>;
